import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  Sparkles,
  MessageSquare,
  User,
  Building2,
  Users,
  Briefcase,
  Clock,
  BadgeCheck,
  Star,
  ChevronDown,
  Lock,
} from "lucide-react";
import HireMeDrawer from "@/components/hirehub/HireMeDrawer";
import HireForMeDrawer from "@/components/hirehub/HireForMeDrawer";
import HireHubAuthDrawer from "@/components/hirehub/HireHubAuthDrawer";
import { useAuth } from "@/contexts/AuthContext";

const PRO_STEPS = [
  { title: "Create Profile", desc: "Add your details and experience preferences" },
  { title: "Get Verified", desc: "Our team verifies your profile for quality network" },
  { title: "Get Matched", desc: "We find suitable opportunities for you" },
  { title: "Dyntra Connects", desc: "We facilitate communication with clinics" },
  { title: "Get Hired", desc: "Start your new journey" },
];

const CLINIC_STEPS = [
  { title: "Submit Requirement", desc: "Tell us who you are looking for" },
  { title: "Requirement Verified", desc: "We validate your requirement" },
  { title: "Profiles Matched", desc: "We find suitable professionals" },
  { title: "Interview", desc: "Connect & interview the matched profiles" },
  { title: "Hire", desc: "Onboard the right dental professional" },
];

const STATS = [
  { icon: Users, value: "12,500+", label: "Verified Dentists" },
  { icon: Building2, value: "2,800+", label: "Clinics & Hospitals" },
  { icon: Briefcase, value: "8,600+", label: "Successful Hires" },
  { icon: Clock, value: "24 hrs", label: "Average Response Time" },
];

const OPPORTUNITIES = [
  { title: "Senior Dentist", city: "Delhi, India", type: "Full-time", exp: "2-4 Yrs Exp", ago: "2 hours ago" },
  { title: "Orthodontist", city: "Noida, India", type: "Full-time", exp: "3-6 Yrs Exp", ago: "5 hours ago" },
  { title: "Visiting Consultant", city: "Mumbai, India", type: "Part-time", exp: "Flexible", ago: "1 day ago" },
];

const PROFESSIONALS = [
  { name: "Dr. Priya Sharma", spec: "Orthodontist", exp: "5 Yrs Exp", city: "Delhi" },
  { name: "Dr. Amit Verma", spec: "Endodontist", exp: "4 Yrs Exp", city: "Noida" },
  { name: "Dr. Neha Patel", spec: "General Dentist", exp: "3 Yrs Exp", city: "Ahmedabad" },
];

const WHY_CHOOSE = [
  { icon: BadgeCheck, title: "Verified Dental Talent", desc: "Every profile is verified for authenticity and credibility." },
  { icon: Sparkles, title: "Faster Shortlisting", desc: "Smart matching to find the right fit, faster." },
  { icon: MessageSquare, title: "Managed Communication", desc: "We handle conversations so you can focus on care." },
  { icon: Star, title: "Quality Matches", desc: "Relevant, qualified and serious professionals only." },
  { icon: Lock, title: "Privacy & Profile Control", desc: "You control what's visible and who can contact you." },
  { icon: ShieldCheck, title: "Trusted Hiring Support", desc: "Our team supports you at every step." },
];

const TESTIMONIALS = [
  {
    quote:
      "I got matched with the right clinic just 5 days after creating my profile. The whole process was smooth and very professional.",
    name: "Dr. Riya Singh",
    role: "Orthodontist",
    rating: 5,
  },
  {
    quote:
      "Dyntra helped us find a great endodontist within 3 days. The quality of profiles and support team are excellent.",
    name: "Dr. Rohit Malhotra",
    role: "Clinic Owner",
    rating: 5,
  },
  {
    quote:
      "As a consultant, I get regular part-time opportunities that fit my schedule perfectly.",
    name: "Dr. Karan Mehta",
    role: "Implantologist",
    rating: 5,
  },
];

const FAQS = [
  { q: "Is creating a profile free for dentists?", a: "Yes, creating and maintaining a professional profile on Hire Hub is completely free for dental professionals." },
  { q: "How does the approval process work?", a: "Our team verifies every profile within 24-48 hours to ensure a quality network of verified professionals." },
  { q: "Can I update my profile later?", a: "Absolutely. You can update your details, experience, location and preferences anytime from your account." },
  { q: "How are clinics verified on Dyntra?", a: "Every hiring clinic goes through a verification step (registration, address, contact) before being approved on the platform." },
  { q: "Is my information safe and private?", a: "Yes. Your contact details are only shared with matched clinics after you approve — never publicly listed." },
];

export default function HireHub() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hireMeOpen, setHireMeOpen] = useState(false);
  const [hireForMeOpen, setHireForMeOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingJourney, setPendingJourney] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  // Support ?open=hireme / ?open=hireforme for deep links.
  useEffect(() => {
    const open = searchParams.get("open");
    if (open === "hireme") startJourney("hire-me");
    if (open === "hireforme") startJourney("hire-for-me");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startJourney = (journey) => {
    if (!user) {
      setPendingJourney(journey);
      setAuthOpen(true);
      return;
    }
    if (journey === "hire-me") setHireMeOpen(true);
    if (journey === "hire-for-me") setHireForMeOpen(true);
  };

  const onAuthenticated = (journey) => {
    if (journey === "hire-me") setHireMeOpen(true);
    if (journey === "hire-for-me") setHireForMeOpen(true);
  };

  return (
    <main data-testid="hire-hub-page">
      {/* Hero */}
      <section className="dyntra-container pt-8">
        <div className="rounded-3xl overflow-hidden bg-[#F5F3FB] border border-[#4B2AAD]/10 p-8 sm:p-12 grid grid-cols-1 md:grid-cols-2 gap-6 items-center relative">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Dental Careers &amp; Hiring
            </h1>
            <p className="text-sm text-gray-600 mt-3 max-w-md">
              Connecting dental professionals with clinics through verified profiles
              and smart matching.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <HeroChip icon={ShieldCheck} title="Verified Profiles" sub="Trust & Safety first" />
              <HeroChip icon={Sparkles} title="Smart Matching" sub="Right match, faster" />
              <HeroChip icon={MessageSquare} title="Managed Communication" sub="We facilitate better connections" />
            </div>
          </div>
          <div className="relative h-56 md:h-72 rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] flex items-center justify-center text-white/90">
            <div className="text-center px-8">
              <ShieldCheck className="h-14 w-14 mx-auto opacity-90" />
              <p className="mt-3 font-display text-lg font-bold">
                Verified network of dental professionals &amp; clinics
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Role */}
      <section className="dyntra-container py-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Choose Your Role
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Select what best describes you to get started
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <RoleCard
            testid="role-hire-me"
            icon={User}
            title="I'm a Dental Professional"
            desc="Create your professional profile and get discovered by clinics for jobs, consultant roles and more opportunities."
            cta="Hire Me"
            perks={["Build one profile", "Get matched with clinics", "Receive opportunities"]}
            onClick={() => startJourney("hire-me")}
          />
          <RoleCard
            testid="role-hire-for-me"
            icon={Building2}
            title="I'm Hiring"
            desc="Submit your hiring requirement and we'll connect you with suitable verified dental professionals."
            cta="Hire For Me"
            perks={["Verified professionals", "Smart matching", "Faster hiring"]}
            onClick={() => startJourney("hire-for-me")}
          />
        </div>
      </section>

      {/* How it works */}
      <section className="dyntra-container pb-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            How It Works
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <StepsPanel title="For Dental Professionals" steps={PRO_STEPS} />
          <StepsPanel title="For Clinics" steps={CLINIC_STEPS} />
        </div>
      </section>

      {/* Stats */}
      <section className="dyntra-container pb-10">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => {
            const Ico = s.icon;
            return (
              <div key={s.label} className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <Ico className="h-5 w-5 text-[#4B2AAD]" />
                </div>
                <div>
                  <p className="font-display text-xl font-extrabold text-gray-900">
                    {s.value}
                  </p>
                  <p className="text-[11px] text-gray-500">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent lists */}
      <section className="dyntra-container pb-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900 mb-3">
            Recently Added Opportunities
          </h3>
          <ul className="space-y-3">
            {OPPORTUNITIES.map((o, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <Briefcase className="h-4 w-4 text-[#4B2AAD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">{o.title}</p>
                  <p className="text-[11px] text-gray-500">
                    {o.city} · {o.type} · {o.exp}
                  </p>
                </div>
                <span className="text-[10px] text-gray-400">{o.ago}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900 mb-3">
            Recently Verified Professionals
          </h3>
          <ul className="space-y-3">
            {PROFESSIONALS.map((p, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {p.name.split(" ").slice(-1)[0].charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">{p.name}</p>
                  <p className="text-[11px] text-gray-500">
                    {p.spec} · {p.exp} · {p.city}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-2 py-0.5">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why Choose */}
      <section className="dyntra-container pb-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Why Choose Dyntra?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {WHY_CHOOSE.map((t) => {
            const Ico = t.icon;
            return (
              <div
                key={t.title}
                className="rounded-2xl border border-gray-100 bg-white p-5 flex items-start gap-3"
              >
                <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
                  <Ico className="h-4 w-4 text-[#4B2AAD]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{t.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Testimonials */}
      <section className="dyntra-container pb-10">
        <div className="text-center mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            What Our Users Say
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5">
              <p className="text-sm text-gray-700 leading-relaxed">“{t.quote}”</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white flex items-center justify-center text-xs font-bold">
                  {t.name.split(" ").pop()[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">{t.name}</p>
                  <p className="text-[11px] text-gray-500">{t.role}</p>
                </div>
                <div className="flex text-[#FFB020]">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="h-3.5 w-3.5 fill-[#FFB020]" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ + CTA */}
      <section className="dyntra-container pb-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900 mb-3">
            Frequently Asked Questions
          </h3>
          <div className="divide-y divide-gray-100">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b border-gray-100 last:border-0">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full py-3 flex items-center justify-between text-left focus:outline-none"
                  >
                    <span className="text-sm font-semibold text-gray-800 pr-4">
                      {f.q}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-500 shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-6 flex flex-col justify-between overflow-hidden">
          <div>
            <h3 className="font-display text-2xl font-extrabold">
              Ready to Get Started?
            </h3>
            <p className="text-sm opacity-90 mt-2 max-w-md">
              Join thousands of dental professionals and clinics who trust Dyntra for
              their career and hiring needs.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => startJourney("hire-me")}
              data-testid="cta-hire-me"
              className="rounded-xl bg-white text-[#4B2AAD] text-sm font-bold px-5 py-2.5 hover:bg-white/90"
            >
              Hire Me →
            </button>
            <span className="text-white/70 text-sm">or</span>
            <button
              onClick={() => startJourney("hire-for-me")}
              data-testid="cta-hire-for-me"
              className="rounded-xl bg-white/10 text-white border border-white/40 text-sm font-bold px-5 py-2.5 hover:bg-white/20"
            >
              Hire For Me →
            </button>
          </div>
        </div>
      </section>

      {/* USP strip */}
      <section className="dyntra-container pb-10">
        <div className="rounded-2xl bg-white border border-gray-100 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Usp icon={ShieldCheck} title="100% Verified" sub="Trusted community" />
          <Usp icon={Lock} title="Secure & Private" sub="Your data is safe" />
          <Usp icon={Users} title="Trusted by Thousands" sub="Growing community" />
          <Usp icon={MessageSquare} title="Support You Can Count On" sub="Every step of the way" />
        </div>
      </section>

      <HireMeDrawer open={hireMeOpen} onOpenChange={setHireMeOpen} />
      <HireForMeDrawer open={hireForMeOpen} onOpenChange={setHireForMeOpen} />
      <HireHubAuthDrawer
        open={authOpen}
        onOpenChange={setAuthOpen}
        journey={pendingJourney}
        onAuthenticated={onAuthenticated}
      />
    </main>
  );
}

function HeroChip({ icon: Icon, title, sub }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-white/60 px-3 py-1.5 backdrop-blur-sm">
      <div className="h-6 w-6 rounded-full bg-[#F3EFFC] flex items-center justify-center">
        <Icon className="h-3 w-3 text-[#4B2AAD]" />
      </div>
      <div className="leading-tight">
        <p className="text-[11px] font-bold text-gray-900">{title}</p>
        <p className="text-[10px] text-gray-500">{sub}</p>
      </div>
    </div>
  );
}

function RoleCard({ icon: Icon, title, desc, cta, perks, onClick, testid }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 flex flex-col">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-2xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
          <Icon className="h-6 w-6 text-[#4B2AAD]" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{desc}</p>
        </div>
      </div>
      <button
        onClick={onClick}
        data-testid={testid}
        className="dyntra-btn-primary self-start mt-4"
      >
        {cta} →
      </button>
      <ul className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
        {perks.map((p) => (
          <li key={p} className="inline-flex items-center gap-1">
            <BadgeCheck className="h-3 w-3 text-[#4B2AAD]" /> {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepsPanel({ title, steps }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <div className="inline-flex items-center rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-3 py-1 uppercase tracking-wider">
        {title}
      </div>
      <div className="mt-4 grid grid-cols-5 gap-2">
        {steps.map((s, i) => (
          <div key={s.title} className="text-center">
            <div className="h-8 w-8 rounded-full bg-[#4B2AAD] text-white text-xs font-bold flex items-center justify-center mx-auto">
              {i + 1}
            </div>
            <p className="mt-2 text-[11px] font-bold text-gray-900 leading-tight">
              {s.title}
            </p>
            <p className="text-[10px] text-gray-500 leading-tight mt-1">
              {s.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Usp({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#4B2AAD]" />
      </div>
      <div>
        <p className="text-sm font-bold text-gray-900">{title}</p>
        <p className="text-[11px] text-gray-500">{sub}</p>
      </div>
    </div>
  );
}
